import React from "react";
import { siteConfig } from "../Datas/productData";
import PolicyNav, { POLICY_UPDATED } from "./PolicyNav";
import "../css/returns.scss";

function Returns() {
  return (
    <main className="policy-page returns">
      <header className="policy-hero">
        <p className="policy-eyebrow">Customer care</p>
        <h1>Returns &amp; refunds</h1>
        <p>
          Eligibility, instructions, exclusions, and timing for returning a
          {` ${siteConfig.brandName}`} purchase.
        </p>
        <span>Last updated {POLICY_UPDATED}</span>
      </header>

      <div className="policy-layout">
        <PolicyNav current="/returns" />

        <article className="policy-content">
          <section className="policy-intro" aria-labelledby="returns-overview">
            <p className="policy-eyebrow">The essentials</p>
            <h2 id="returns-overview">A 30-day return window.</h2>
            <p>
              You may request a return for an eligible item within 30 calendar
              days after delivery. The item must be unused, unwashed, and in
              resalable condition with its original tags, accessories, and
              packaging.
            </p>
          </section>

          <section className="policy-cards" aria-label="Return highlights">
            <div>
              <span>01</span>
              <h3>30 days</h3>
              <p>From confirmed delivery to contact us about a return.</p>
            </div>
            <div>
              <span>02</span>
              <h3>Original condition</h3>
              <p>Unused, unwashed, and complete with tags and packaging.</p>
            </div>
            <div>
              <span>03</span>
              <h3>5–10 days</h3>
              <p>Typical bank processing after we issue an approved refund.</p>
            </div>
          </section>

          <section className="policy-section" aria-labelledby="return-start">
            <div className="policy-section__heading">
              <span>01</span>
              <h2 id="return-start">Start a return</h2>
            </div>
            <ol className="policy-steps">
              <li>
                <span>1</span>
                <div>
                  <h3>Email us first</h3>
                  <p>
                    Send your order number, the item you wish to return, and the
                    reason to {siteConfig.contact.email}. Include photographs if
                    the item is damaged, defective, or incorrect.
                  </p>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <h3>Wait for instructions</h3>
                  <p>
                    We will confirm eligibility and provide the correct return
                    address and reference. Do not send a parcel to an address
                    found elsewhere unless customer care has confirmed it.
                  </p>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <h3>Send it with tracking</h3>
                  <p>
                    Package the item securely and keep the carrier receipt. An
                    unapproved or untracked return may be delayed or refused.
                  </p>
                </div>
              </li>
            </ol>
          </section>

          <section className="policy-section" aria-labelledby="return-eligible">
            <div className="policy-section__heading">
              <span>02</span>
              <h2 id="return-eligible">Eligibility &amp; condition</h2>
            </div>
            <p>
              Returned merchandise must show no signs of use, wear, washing,
              alteration, fragrance, stains, or damage after delivery. Sets must
              be returned as a complete set. We may reasonably reduce or decline
              a refund if an item has lost value because it was handled beyond
              what is necessary to inspect it, where permitted by law.
            </p>
            <p>
              Proof of purchase is required. This policy covers purchases made
              directly through this website; purchases from another retailer
              are governed by that retailer’s policy.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="return-exclusions">
            <div className="policy-section__heading">
              <span>03</span>
              <h2 id="return-exclusions">Non-returnable items</h2>
            </div>
            <p>Unless faulty, misdescribed, or otherwise protected by law, we do not accept returns of:</p>
            <ul>
              <li>personalized, monogrammed, altered, or made-to-order products;</li>
              <li>gift cards and digital products;</li>
              <li>sealed hygiene-sensitive goods after the seal is opened;</li>
              <li>final-sale items clearly identified before purchase; or</li>
              <li>items damaged through misuse, improper care, or ordinary wear.</li>
            </ul>
          </section>

          <section className="policy-section" aria-labelledby="return-costs">
            <div className="policy-section__heading">
              <span>04</span>
              <h2 id="return-costs">Return shipping &amp; original delivery fees</h2>
            </div>
            <p>
              For a change-of-mind return, you are responsible for return
              postage and the original shipping charge is not refundable. If we
              confirm that an item arrived damaged, defective, or incorrect, we
              will provide a prepaid method or reimburse reasonable standard
              return postage. We are not responsible for a customer-arranged
              return until it reaches the return location we provide.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="return-refunds">
            <div className="policy-section__heading">
              <span>05</span>
              <h2 id="return-refunds">Inspection, refunds &amp; exchanges</h2>
            </div>
            <p>
              We normally inspect a received return within seven business days.
              If approved, we issue the refund to the original payment method
              and email you. Your bank or card issuer may need another 5–10
              business days to post it. Any duties or taxes paid directly to a
              government authority must be reclaimed from that authority.
            </p>
            <p>
              We do not currently process direct exchanges. Return the eligible
              item and place a separate order for the preferred color or size so
              availability is not held up during transit.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="return-faults">
            <div className="policy-section__heading">
              <span>06</span>
              <h2 id="return-faults">Faults, cancellations &amp; legal rights</h2>
            </div>
            <p>
              Contact us promptly if an order is damaged, defective, incomplete,
              or not what you ordered. If an order has not entered processing,
              we will try to cancel it; cancellation is not guaranteed after
              processing begins. Nothing in this policy excludes or restricts a
              remedy that cannot lawfully be excluded in your location.
            </p>
          </section>

          <section className="policy-help" aria-labelledby="return-help">
            <div>
              <p className="policy-eyebrow">Ready to begin?</p>
              <h2 id="return-help">Tell us what did not work.</h2>
            </div>
            <a href={`mailto:${siteConfig.contact.email}`}>
              Request a return <span aria-hidden="true">↗</span>
            </a>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Returns;
